import {
  type HhDataItem,
  type SiteDemandView,
} from "@inrange/building-manager-api-client";
import { getESysName } from "@inrange/building-manager-api-client/models-site";
import { MouseEvent, useState } from "react";
import { Button } from "react-bootstrap";
import { Link } from "react-router-dom";

export type { HhDataItem };

interface HhDataRowProps {
  item: HhDataItem;
  onEditClick?: (item: HhDataItem) => void;
  onViewConfigClick?: (item: HhDataItem) => void;
  onAssignToSitesClick?: (item: HhDataItem) => void;
  onRemoveFromSitesClick?: (item: HhDataItem) => void;
  onDeleteClick?: (item: HhDataItem) => void;
  onDownloadClick?: (item: HhDataItem) => Promise<{ url: string }>;
  onReviewAnalysisClick?: (item: HhDataItem) => void;
  showActions?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (checked: boolean) => void;
}

const renderFillCoverageBadge = (fraction: number | undefined) => {
  if (fraction === undefined) return <span className="text-muted">—</span>;
  const pct = fraction * 100;
  // Green >=85, yellow 70-85, red <70 - matches the existing site-level alert at 85%.
  const bg = pct >= 85 ? "success" : pct >= 70 ? "warning" : "danger";
  return (
    <span
      className={`badge bg-${bg}`}
      title={`${pct.toFixed(1)}% of the normalised year is direct customer data; the rest was filled to reach a full year.`}
    >
      {pct.toFixed(0)}%
    </span>
  );
};

const HhDataRow = ({
  item,
  onEditClick,
  onViewConfigClick,
  onAssignToSitesClick,
  onRemoveFromSitesClick,
  onDeleteClick,
  onDownloadClick,
  onReviewAnalysisClick,
  showActions = true,
  isSelected = false,
  onSelectionChange,
}: HhDataRowProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!onDownloadClick || isDownloading) return;

    setIsDownloading(true);
    try {
      const response = await onDownloadClick(item);
      if (!response?.url) return;

      // Create a download link and click it
      const link = document.createElement("a");
      link.href = response.url;
      link.setAttribute("download", item.fileName || "hh-data");
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode?.removeChild(link);
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDateTime = (epochSeconds: number | undefined) => {
    if (!epochSeconds) return "—";
    // Convert epoch seconds to milliseconds for JavaScript Date
    return new Date(epochSeconds * 1000).toLocaleString();
  };

  const getDisplayLabel = (site: SiteDemandView, demandIdx: number): string => {
    const demandCount = Object.keys(site.demand).length;
    if (demandCount === 1) {
      return site.name;
    }

    // Find the esys that maps to this demandIdx
    const esysCount = Object.keys(site.esys).length;
    for (const esys of Object.values(site.esys)) {
      // Use Number() to handle potential string/number type mismatch from JSON data
      if (Number(esys.demandIdx) === demandIdx) {
        const esysName = getESysName(esysCount, esys);
        return `${site.name} -> ${esysName.trim() || `Demand ${demandIdx + 1}`}`;
      }
    }

    return `${site.name} -> Demand ${demandIdx + 1}`;
  };

  const getOwnerships = (site: SiteDemandView): string[] => {
    return site.siteOwnerships
      .filter((o) => o.name)
      .map((o) => o.name as string)
      .sort((a, b) => a.localeCompare(b));
  };

  const renderSites = () => {
    if (item.siteMappings.length === 0) return "—";

    // Sort sites by display label
    const sortedSiteIds = [...item.siteMappings].sort((a, b) => {
      const labelA = getDisplayLabel(a.site, a.demandIdx).toLowerCase();
      const labelB = getDisplayLabel(b.site, b.demandIdx).toLowerCase();
      return labelA.localeCompare(labelB);
    });

    return (
      <ul style={{ margin: 0, paddingLeft: "16px" }}>
        {sortedSiteIds.map((s) => {
          const ownerships = getOwnerships(s.site);
          return (
            <li key={`${s.site.id}-${s.demandIdx}`}>
              <Link to={`/site/${s.site.id}/edit`}>
                {getDisplayLabel(s.site, s.demandIdx)}
              </Link>
              {ownerships.length > 0 && (
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "16px",
                  }}
                >
                  {ownerships.map((org, idx) => (
                    <li key={idx}>{org}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const canDownload = !item.isScript && item.fileName !== "unknown";

  return (
    <tr>
      {onSelectionChange && (
        <td>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelectionChange(e.target.checked)}
          />
        </td>
      )}
      <td>
        <div>{item.fileName || "—"}</div>
        {item.autoMode?.source === "multi_curve_split" && (
          <div className="mt-1">
            <span
              className="badge bg-info"
              title="One curve split out of a multi-curve file"
            >
              Multi-curve file
            </span>
          </div>
        )}
        {item.autoMode?.autoFixed && (
          <div className="mt-1">
            <span
              className="badge bg-success"
              title={
                item.autoMode.autoFixedBy
                  ? `Healed by an AI-proposed fix (${item.autoMode.autoFixedBy})`
                  : "Healed by an AI-proposed fix"
              }
            >
              Auto-fixed with AI
            </span>
          </div>
        )}
        {item.latestAnalysis && onReviewAnalysisClick && (
          <div className="mt-1">
            <Button
              size="sm"
              variant="outline-success"
              onClick={() => onReviewAnalysisClick(item)}
              className="py-0"
              style={{ fontSize: "0.75em" }}
            >
              Review file analysis
            </Button>
          </div>
        )}
        {canDownload && (
          <Button
            variant="link"
            size="sm"
            onClick={handleDownload}
            disabled={isDownloading}
            style={{ padding: 0, marginTop: "4px" }}
          >
            {isDownloading ? "Downloading..." : "Download"}
          </Button>
        )}
        <div style={{ fontSize: "0.7em", color: "#666" }}>{item.guid}</div>
      </td>
      <td>{item.uploadedBy ? item.uploadedBy : "-"}</td>
      <td>
        {formatDateTime(item.processedTime)}
        {item.processedBy ? (
          <>
            <br />
            {item.processedBy}
          </>
        ) : null}
        {item.autoMode && (
          <>
            <br />
            <span
              className="badge bg-success"
              title={
                item.autoMode.detection
                  ? `Auto mode (v${item.autoMode.version}): period=${item.autoMode.detection.period.value} (${item.autoMode.detection.period.confidence}), unit=${item.autoMode.detection.unit.value} (${item.autoMode.detection.unit.confidence})`
                  : `Auto mode (v${item.autoMode.version})${item.autoMode.source ? ` - source: ${item.autoMode.source}` : ""}`
              }
            >
              Auto
            </span>
          </>
        )}
      </td>
      <td>{renderFillCoverageBadge(item.customerDataFraction)}</td>
      <td>{renderSites()}</td>
      {showActions && (
        <td style={{ width: "25%" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
              }}
            >
              <Button
                variant="primary"
                size="sm"
                onClick={() => onAssignToSitesClick?.(item)}
              >
                Assign to sites
              </Button>
              {item.siteMappings.length > 0 && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => onRemoveFromSitesClick?.(item)}
                >
                  Remove from sites
                </Button>
              )}
              {item.siteMappings.length === 0 && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => onDeleteClick?.(item)}
                >
                  Delete
                </Button>
              )}
            </div>
            {!item.isScript && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px",
                }}
              >
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onViewConfigClick?.(item)}
                >
                  View config
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onEditClick?.(item)}
                >
                  Copy for re-processing
                </Button>
              </div>
            )}
          </div>
        </td>
      )}
    </tr>
  );
};

export default HhDataRow;
